import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CircularProgress, Dialog } from '@mui/material';
import { FiArrowLeft, FiRefreshCw, FiUsers } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import ProfileIdentity from '../../../components/content/ProfileIdentity';
import PlainImage from '../../../features/images/PlainImage';
import { useUserStore } from '../../../stores/userStore';
import { useWebLocale } from '../../../contexts/WebLocaleContext';
import { saveCurrentPathAndRedirectToLogin } from '../../../utils/authUtils';
import useFollowList from './useFollowList';
import { useMemberEntry } from '../../../features/members/memberMemory';
import './FollowListDialog.css';

function FollowContent(props) {
  const { nickname, onClose, initialTab, viewer, en, entry, restored, onProfileClose } = props;
  const saved = restored ? entry.follow : null;
  const model = useFollowList({ ...props, initialSnapshot: saved?.model }),
    navigate = useNavigate();
  const [tab, setTab] = useState(
    saved?.tab || (initialTab === 'following' ? 'following' : 'followers'),
  );
  const [visible, setVisible] = useState(saved?.visible || { followers: 30, following: 30 });
  const scroll = useRef(null),
    positions = useRef(saved?.positions || { followers: 0, following: 0 });
  const focusId = useRef(saved?.focusId);
  useLayoutEffect(() => {
    entry.follow = {
      open: true,
      nickname,
      tab,
      visible,
      positions: positions.current,
      model: { lists: model.lists, counts: model.counts },
      focusId: focusId.current,
    };
  });
  useEffect(() => {
    if (!focusId.current) return;
    const target = document.querySelector(
      `[data-follow-id="${Number(focusId.current)}"] .pp-follow-identity`,
    );
    if (target) {
      target.focus({ preventScroll: true });
      focusId.current = null;
    }
  }, [model.lists]);
  const labels = { followers: en ? 'Followers' : '팔로워', following: en ? 'Following' : '팔로잉' };
  const state = model.lists[tab],
    items = state.items.slice(0, visible[tab]);
  const more = () =>
    setVisible((s) => ({ ...s, [tab]: Math.min(state.items.length, s[tab] + 30) }));
  const select = (next) => {
    if (next === tab) return;
    positions.current[tab] = scroll.current?.scrollTop || 0;
    setTab(next);
  };
  const error = en
    ? `We could not load ${labels[tab]}.`
    : `${labels[tab]} 목록을 불러오지 못했습니다.`;
  return (
    <>
      <header className="pp-follow-header">
        <button onClick={onClose} aria-label={en ? 'Back' : '뒤로가기'}>
          <FiArrowLeft size={22} />
        </button>
        <h1 id="pp-follow-title">{nickname}</h1>
        <button
          onClick={() => model.refresh(tab)}
          disabled={state.loading || model.busy !== null}
          aria-label={en ? 'Refresh' : '새로고침'}
        >
          <FiRefreshCw size={18} />
        </button>
      </header>
      <div className="pp-follow-tabs" role="tablist" aria-label={en ? 'Connections' : '팔로우'}>
        {['followers', 'following'].map((key) => (
          <button
            key={key}
            id={`pp-follow-tab-${key}`}
            role="tab"
            aria-selected={tab === key}
            aria-controls={`pp-follow-panel-${key}`}
            tabIndex={tab === key ? 0 : -1}
            onKeyDown={(e) => {
              if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                e.preventDefault();
                const next =
                  e.key === 'Home'
                    ? 'followers'
                    : e.key === 'End'
                      ? 'following'
                      : key === 'followers'
                        ? 'following'
                        : 'followers';
                select(next);
                document.getElementById(`pp-follow-tab-${next}`)?.focus();
              }
            }}
            onClick={() => select(key)}
          >
            {labels[key]} {model.counts[key] ?? model.lists[key].items.length}
          </button>
        ))}
      </div>
      {model.mutationError && (
        <p className="pp-follow-error" role="alert">
          {en
            ? 'Could not update following. Try again.'
            : '팔로우 상태를 변경하지 못했어요. 다시 시도해 주세요.'}
        </p>
      )}
      <div
        key={tab}
        ref={(el) => {
          scroll.current = el;
          if (el) el.scrollTop = positions.current[tab];
        }}
        className="pp-follow-scroll"
        role="tabpanel"
        id={`pp-follow-panel-${tab}`}
        aria-labelledby={`pp-follow-tab-${tab}`}
        tabIndex={0}
        onScroll={(e) => {
          const el = e.currentTarget;
          positions.current[tab] = el.scrollTop;
          if (
            el.scrollHeight - el.scrollTop - el.clientHeight < 120 &&
            items.length < state.items.length
          )
            more();
        }}
      >
        {state.loading && !items.length && (
          <div
            className="pp-follow-loading"
            role="status"
            aria-label={en ? 'Loading' : '불러오는 중'}
          >
            <CircularProgress size={24} />
          </div>
        )}
        {!state.loading && !state.error && !items.length && (
          <div className="pp-follow-empty">
            <FiUsers size={30} />
            <strong>
              {tab === 'followers'
                ? en
                  ? 'No followers yet'
                  : '아직 팔로워가 없어요'
                : en
                  ? 'Not following anyone yet'
                  : '아직 팔로잉한 회원이 없어요'}
            </strong>
            <p>
              {tab === 'followers'
                ? en
                  ? 'Share your music stories to meet new followers.'
                  : '음악 이야기를 나누면 새로운 팔로워를 만날 수 있어요.'
                : en
                  ? 'Follow members whose taste interests you.'
                  : '마음에 드는 회원을 팔로우해 보세요.'}
            </p>
          </div>
        )}
        <div className="pp-follow-list">
          {items.map((user) => (
            <div className="pp-follow-row" key={user.id} data-follow-id={user.id}>
              <button
                className="pp-follow-identity"
                aria-label={en ? `Open ${user.nickname}’s profile` : `${user.nickname} 프로필 보기`}
                onClick={() => {
                  positions.current[tab] = scroll.current?.scrollTop || 0;
                  entry.follow.focusId = user.id;
                  onProfileClose();
                  navigate(`/users/${encodeURIComponent(user.nickname)}`);
                }}
              >
                <span className="pp-follow-avatar" aria-hidden="true">
                  {user.profileImageUrl?.trim() ? (
                    <PlainImage src={user.profileImageUrl} alt="" />
                  ) : (
                    user.nickname?.trim()[0]?.toUpperCase() || 'P'
                  )}
                </span>
                <span className="pp-follow-copy">
                  <span className="pp-follow-name">
                    <ProfileIdentity name={user.nickname} mark={user.profileMark} avatar={false} />
                  </span>
                  <span className="pp-follow-bio">
                    {user.bio?.trim() ||
                      `${en ? 'Followers' : '팔로워'} ${user.followersCount ?? 0}`}
                  </span>
                </span>
              </button>
              {String(user.id) !== String(viewer.id) && (
                <button
                  className={`pp-follow-action${user.isFollowing ? ' is-following' : ''}`}
                  disabled={
                    model.busy !== null ||
                    model.lists.followers.loading ||
                    model.lists.following.loading
                  }
                  aria-label={`${user.nickname} ${user.isFollowing ? (en ? 'Unfollow' : '팔로우 취소') : en ? 'Follow' : '팔로우'}`}
                  onClick={() => model.toggle(user)}
                >
                  {model.busy === user.id ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : user.isFollowing ? (
                    en ? (
                      'Following'
                    ) : (
                      '팔로우 중'
                    )
                  ) : en ? (
                    'Follow'
                  ) : (
                    '팔로우'
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
        {state.error && (
          <div className="pp-follow-error" role="alert">
            <p>{error}</p>
            <button disabled={state.loading} onClick={() => model.refresh(tab)}>
              {en ? 'Try again' : '다시 시도'}
            </button>
          </div>
        )}
        {items.length < state.items.length && (
          <button className="pp-follow-more" onClick={more}>
            {en ? 'Show more' : '더 보기'}
          </button>
        )}
      </div>
    </>
  );
}

export default function FollowListDialog(props) {
  const viewer = useUserStore((s) => s.user),
    { locale } = useWebLocale(),
    navigate = useNavigate();
  const en = locale === 'en';
  const { entry, returning, location } = useMemberEntry('follow');
  const [returnState, setReturnState] = useState(() => ({
    entry,
    open: Boolean(returning && entry.follow?.open),
  }));
  if (returnState.entry !== entry)
    setReturnState({ entry, open: Boolean(returning && entry.follow?.open) });
  const restored =
    returnState.entry === entry && returnState.open && entry.follow?.nickname === props.nickname;
  const close = () => {
    delete entry.follow;
    setReturnState({ entry, open: false });
    props.onClose();
  };
  if (!props.open && !restored) return null;
  const authenticated = viewer?.id != null && Boolean(localStorage.getItem('POSTPOP_AUTH_TOKEN'));
  return (
    <Dialog
      open
      onClose={close}
      fullWidth
      maxWidth={false}
      className="pp-follow-dialog"
      aria-labelledby="pp-follow-title"
    >
      {authenticated ? (
        <FollowContent
          key={`${location.key}:${props.nickname}:${viewer.id}:${entry.version}`}
          {...props}
          onClose={close}
          onProfileClose={props.onClose}
          entry={entry}
          restored={restored}
          viewer={viewer}
          en={en}
        />
      ) : (
        <>
          <header className="pp-follow-header">
            <button onClick={close} aria-label={en ? 'Back' : '뒤로가기'}>
              <FiArrowLeft size={22} />
            </button>
            <h1 id="pp-follow-title">{en ? 'Connections' : '팔로우'}</h1>
          </header>
          <div className="pp-follow-empty">
            <p>{en ? 'Please log in to continue.' : '로그인이 필요해요.'}</p>
            <button
              className="pp-follow-action"
              onClick={() => {
                close();
                saveCurrentPathAndRedirectToLogin(navigate);
              }}
            >
              {en ? 'Log in' : '로그인'}
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}
